import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ProgressSpinner } from "primereact/progressspinner";

import { useLazyGetStationOptionInfoQuery } from "../../../../redux/services/foodStationOptions";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "./MenuItemInfoModal.module.css";

const HIDE_DELAY_MS = 150;
const TOOLTIP_WIDTH = 320;
const VIEWPORT_PADDING = 12;

export const shouldShowMenuItemInfo = (name) => {
  if (!name) return false;
  const trimmed = String(name).trim().toLowerCase();
  if (trimmed === "no" || trimmed === "none") return false;
  if (trimmed.startsWith("no ")) return false;
  return true;
};

const prefersHover = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover)").matches;

export const MenuItemInfoIcon = ({ optionId, itemName }) => {
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [trigger, { data, isFetching, isError }] =
    useLazyGetStationOptionInfoQuery();

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [open]);


  useEffect(() => {
    if (!open || prefersHover()) return;
    const onDocClick = (e) => {
      if (iconRef.current?.contains(e.target)) return;
      if (tooltipRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  if (!optionId || !shouldShowMenuItemInfo(itemName)) return null;

  const computePosition = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return { top: 0, left: 0 };
    const left = Math.min(
      Math.max(
        rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
        VIEWPORT_PADDING,
      ),
      window.innerWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING,
    );
    return { top: rect.bottom + 8, left };
  };

  const show = () => {
    clearTimeout(hideTimer.current);
    setCoords(computePosition());
    trigger(
      { id: optionId, subDomain: getSubdomain() },
      true, // preferCacheValue — skip refetch when fresh
    );
    setOpen(true);
  };

  const scheduleHide = () => {
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  const stop = (e) => {
    e.stopPropagation();
  };

  const renderBody = () => {
    if (isFetching && !data) {
      return (
        <div className={styles.loaderWrapper}>
          <ProgressSpinner style={{ width: 28, height: 28 }} strokeWidth="4" />
        </div>
      );
    }
    if (isError) {
      return <p className={styles.error}>Unable to load description.</p>;
    }
    if (!data?.available) {
      return <p className={styles.empty}>No description available.</p>;
    }
    return (
      <>
        {data.name && <p className={styles.title}>{data.name}</p>}
        {data.image && (
          <img
            className={styles.image}
            src={data.image}
            alt={data.title || data.name}
            loading="lazy"
          />
        )}
        {data.description && (
          <p className={styles.description}>{data.description}</p>
        )}
        {data.sourceUrl && (
          <a
            className={styles.sourceLink}
            href={data.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
          >
            {data.source === "custom" ? "More info" : "Wikipedia"}{" "}
            <i className="pi pi-external-link" />
          </a>
        )}
      </>
    );
  };

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        className={styles.infoIconBtn}
        onMouseEnter={() => prefersHover() && show()}
        onMouseLeave={() => prefersHover() && scheduleHide()}
        onClick={(e) => {
          e.preventDefault();
          stop(e);
          open ? setOpen(false) : show();
        }}
        onMouseDown={stop}
        onTouchStart={stop}
        aria-label="Show item info"
      >
        <i className="pi pi-info-circle" />
      </button>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            className={styles.tooltip}
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={() =>
              prefersHover() && clearTimeout(hideTimer.current)
            }
            onMouseLeave={() => prefersHover() && scheduleHide()}
            role="tooltip"
          >
            {renderBody()}
          </div>,
          document.body,
        )}
    </>
  );
};
